import { ButtonType, ButtonWidget, TabStyles } from '@tableau/widgets';
import React from 'react';
import { style } from 'typestyle';

import { Messages } from '../../../../codegen/Localize';
import { ClearOrphanedManagedKeychainAction } from '../../../../connectivity/services/ClearOrphanedManagedKeychainAction';
import WebHttpClient from '../../../../httpClient/WebHttpClient';
import { getSessionManagementServiceInstance, SessionManagementService } from '../../../../sessionManagement/SessionManagementService';
import { L10n } from '../../../../ts/L10n/L10n';
import { GetNumManagedKeychainsRequest, IGetNumManagedKeychainResult } from '../../../../ts/ServerApi/requests/GetNumManagedKeychainRequest';
import { SettingsSection } from '../../../components/common/SettingsSection';
import { formError } from '../../../styles/CommonStyles';
import { componentRegistryEntry } from '../SettingsComponentRegistryFunctions';

export type ManagedKeychainCleanupDependencies = {
  sessionManagementService: SessionManagementService;
  keyChainAction: ClearOrphanedManagedKeychainAction;
}

const cleanButtonStyle = style({
  marginTop: TabStyles.Spacing.MediumSpace
});

const countButtonStyle = style({
  marginTop: TabStyles.Spacing.SmallSpace
});

/**
 * Component with dependencies injected as props
 */
function InternalManagedKeychainCleanupSection(props: ManagedKeychainCleanupDependencies) {
  const [counts, setCounts] = React.useState<IGetNumManagedKeychainResult>(null);
  const [fetching, setFetching] = React.useState(false);
  const countActionButtonHandler = useCountButtonHandler(setCounts, setFetching);
  const clearActionButtonHandler = useClearButtonHandler(setCounts,props.keyChainAction);

  return (
    <SettingsSection title={Messages.managedKeychainCleanUpGroupName()}
                     description={Messages.managedKeychainCleanUpMsg()}>
      <div>
        <ButtonWidget buttonType={ButtonType.Outline}
                      testId={'managed-keychain-count-on-site-button'}
                      className={countButtonStyle}
                      onClick={countActionButtonHandler}
                      disabled={fetching}
                      type={'button'}>
                        { fetching
        ? Messages.managedKeychainCountOnSiteFetching() : Messages.managedKeychainCountOnSite()}
        </ButtonWidget>

        {
          counts &&
          <div className={formError} data-tb-test-id={'managed-keychain-count'}>
            {
              Messages.managedKeychainCountValuesMsg({
                TOTAL_COUNT: L10n.localizedCount(counts.totalManagedKeychainCount),
                ORPHANED_COUNT: L10n.localizedCount(counts.orphanedManagedKeychainCount),
              })
            }
          </div>
        }
      </div>
      <div>
          <ButtonWidget buttonType={ButtonType.Outline}
                      testId={'clear-orphaned-managed-keychain'}
                      className={cleanButtonStyle}
                      onClick={clearActionButtonHandler}
                      type={'button'}>
            {Messages.clearOrphanedManagedKeychain_action()}
          </ButtonWidget>
      </div>
    </SettingsSection>
  );
}

function useCountButtonHandler(setCounts: (result: IGetNumManagedKeychainResult) => void, setFetching: (result: boolean) => void){
  return React.useCallback(async () => {
    setCounts(null);
    setFetching(true);
    const keychainCounts = await WebHttpClient.sendRequest(new GetNumManagedKeychainsRequest({}));
    setCounts(keychainCounts);
    setFetching(false);
  }, [setCounts, setFetching]);
}


function useClearButtonHandler(setCounts: (result: IGetNumManagedKeychainResult) => void, keyChainAction: ClearOrphanedManagedKeychainAction) {
  return React.useCallback(
    () => {
      setCounts(null);
      keyChainAction.confirmDeleteOrphanedManagedKeychain();
    },
    // This deps array was noncompliant before we enabled the lint rule. If you're here, please consider fixing it! See TFS 1257824
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
}

/**
 * A wrapper component which provides the dependencies to the props
 * @param props
 * @constructor
 */
export function ManagedKeychainCleanupSection() {
  const deps: ManagedKeychainCleanupDependencies = {
    sessionManagementService: getSessionManagementServiceInstance(),
    keyChainAction: ClearOrphanedManagedKeychainAction
  };
  return <InternalManagedKeychainCleanupSection {...deps}/>;
}

export const ManagedKeychainCleanupSectionSpecs = componentRegistryEntry({
  name: 'ManagedKeychainCleanupSection',
  component: ManagedKeychainCleanupSection
});
